import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Loader2, ArrowLeft, Pencil, Trash2, Package } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useClinic } from '@/contexts/ClinicContext';
import { useNavigate } from 'react-router-dom';
import {
  practiceProductsService,
  type PracticeProduct,
  type CreatePracticeProductInput,
} from '@/services/practiceProductsService';

const CATEGORY_OPTIONS = [
  { value: 'visit', label: 'Visit' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'product', label: 'Product' },
  { value: 'lab', label: 'Lab' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_COLORS: Record<string, string> = {
  visit: 'bg-blue-100 text-blue-800',
  procedure: 'bg-purple-100 text-purple-800',
  product: 'bg-green-100 text-green-800',
  lab: 'bg-orange-100 text-orange-800',
  other: 'bg-gray-100 text-gray-800',
};

const PRICE_MAX_DOLLARS = 999999.99;
const NAME_MAX_LENGTH = 100;

const productFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(NAME_MAX_LENGTH, `Name must be ${NAME_MAX_LENGTH} characters or less`),
  description: z.string().optional(),
  category: z.enum(['visit', 'procedure', 'product', 'lab', 'other']),
  priceDollars: z
    .string()
    .min(1, 'Price is required')
    .regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid price (up to 2 decimals)')
    .refine((v) => parseFloat(v) <= PRICE_MAX_DOLLARS, {
      message: `Price cannot exceed $${PRICE_MAX_DOLLARS.toLocaleString()}`,
    }),
  cptCode: z
    .string()
    .refine((v) => v === '' || /^\d{5}$/.test(v.trim()), {
      message: 'CPT code must be exactly 5 digits',
    }),
  icdCodes: z.string().optional(),
});

type ProductFormData = z.infer<typeof productFormSchema>;

const defaultFormValues: ProductFormData = {
  name: '',
  description: '',
  category: 'other',
  priceDollars: '',
  cptCode: '',
  icdCodes: '',
};

export default function PracticeProductsPage() {
  const { clinic } = useClinic();
  const { toast } = useToast();
  const navigate = useNavigate();
  const clinicId = clinic?.id;

  const [products, setProducts] = useState<PracticeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProduct, setEditingProduct] = useState<PracticeProduct | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productFormSchema),
    defaultValues: defaultFormValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const nameValue = watch('name') ?? '';

  const fetchProducts = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    try {
      const result = await practiceProductsService.list(clinicId, {
        search: searchQuery || undefined,
        category: filterCategory !== 'all' ? filterCategory : undefined,
        activeOnly: true,
      });
      setProducts(result.products);
    } catch {
      toast({ title: 'Error', description: 'Failed to load practice products', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [clinicId, searchQuery, filterCategory, toast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (!dialogOpen) {
      reset(defaultFormValues);
      setEditingProduct(null);
    }
  }, [dialogOpen, reset]);

  const openCreateDialog = () => {
    setEditingProduct(null);
    reset(defaultFormValues);
    setDialogOpen(true);
  };

  const openEditDialog = (product: PracticeProduct) => {
    setEditingProduct(product);
    reset({
      name: product.name,
      description: product.description || '',
      category: (product.category as ProductFormData['category']) || 'other',
      priceDollars: product.price_cents ? (product.price_cents / 100).toFixed(2) : '',
      cptCode: product.default_cpt_code || '',
      icdCodes: (product.default_icd_codes || []).join(', '),
    });
    setDialogOpen(true);
  };

  const onSubmit = async (data: ProductFormData) => {
    if (!clinicId) return;
    setSaving(true);
    try {
      const icdArray = (data.icdCodes || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const priceCents = Math.round(parseFloat(data.priceDollars) * 100);
      const cpt = data.cptCode.trim();

      if (editingProduct) {
        await practiceProductsService.update(clinicId, editingProduct.id, {
          name: data.name.trim(),
          description: data.description || undefined,
          category: data.category,
          price_cents: priceCents,
          default_cpt_code: cpt || null,
          default_icd_codes: icdArray.length > 0 ? icdArray : null,
        });
        toast({ title: 'Updated', description: 'Practice product updated' });
      } else {
        const input: CreatePracticeProductInput = {
          name: data.name.trim(),
          priceCents,
          category: data.category,
          cptCode: cpt || undefined,
          icdCodes: icdArray.length > 0 ? icdArray : undefined,
          description: data.description || undefined,
        };
        await practiceProductsService.create(clinicId, input);
        toast({ title: 'Created', description: 'Practice product created' });
      }
      setDialogOpen(false);
      fetchProducts();
    } catch {
      toast({ title: 'Error', description: 'Failed to save practice product', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product: PracticeProduct) => {
    if (!clinicId) return;
    try {
      await practiceProductsService.delete(clinicId, product.id);
      toast({ title: 'Deactivated', description: `${product.name} has been deactivated` });
      fetchProducts();
    } catch {
      toast({ title: 'Error', description: 'Failed to deactivate product', variant: 'destructive' });
    }
  };

  const formatPrice = (cents: number) => {
    if (!cents) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const category = watch('category');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Practice Products</h1>
            <p className="text-muted-foreground">Manage your billable services and items catalog</p>
          </div>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Product
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Input
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-xs"
        />
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Product List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : products.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No practice products yet</h3>
            <p className="text-muted-foreground mb-4">Create your first billable service or item</p>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              Add Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <Card key={product.id} className="relative">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{product.name}</CardTitle>
                    <Badge className={`mt-1 ${CATEGORY_COLORS[product.category] || CATEGORY_COLORS.other}`}>
                      {product.category}
                    </Badge>
                  </div>
                  <span className="text-lg font-bold">{formatPrice(product.price_cents)}</span>
                </div>
              </CardHeader>
              <CardContent>
                {product.description && (
                  <p className="text-sm text-muted-foreground mb-2">{product.description}</p>
                )}
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {product.default_cpt_code && <span>CPT: {product.default_cpt_code}</span>}
                  {product.default_icd_codes?.length ? (
                    <span>ICD: {product.default_icd_codes.join(', ')}</span>
                  ) : null}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(product)}>
                    <Pencil className="mr-1 h-3 w-3" />
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(product)}>
                    <Trash2 className="mr-1 h-3 w-3" />
                    Deactivate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Practice Product' : 'New Practice Product'}</DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Update this billable service or item.' : 'Add a new billable service or item to your catalog.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="name" required>Name</Label>
                <span
                  className={cn(
                    'text-xs',
                    nameValue.length >= NAME_MAX_LENGTH ? 'text-red-600' : 'text-slate-500'
                  )}
                >
                  {nameValue.length}/{NAME_MAX_LENGTH}
                </span>
              </div>
              <Input
                id="name"
                placeholder="e.g., 30-Minute Office Visit"
                maxLength={NAME_MAX_LENGTH}
                className={errors.name ? 'border-red-500' : ''}
                {...register('name')}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description"
                rows={2}
                {...register('description')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={category}
                  onValueChange={(v) => setValue('category', v as ProductFormData['category'], { shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="price" required>Price ($)</Label>
                <Input
                  id="price"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  className={errors.priceDollars ? 'border-red-500' : ''}
                  {...register('priceDollars')}
                />
                {errors.priceDollars && (
                  <p className="text-sm text-red-600">{errors.priceDollars.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="cptCode">Default CPT Code</Label>
                <Input
                  id="cptCode"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="e.g., 99213"
                  className={errors.cptCode ? 'border-red-500' : ''}
                  {...register('cptCode')}
                />
                {errors.cptCode && (
                  <p className="text-sm text-red-600">{errors.cptCode.message}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="icdCodes">Default ICD-10 Codes</Label>
                <Input
                  id="icdCodes"
                  placeholder="e.g., Z00.00, E11.9"
                  {...register('icdCodes')}
                />
              </div>
            </div>

            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingProduct ? 'Save Changes' : 'Create Product'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
